/**
 * Route Page 3: Prompt for Starting and Ending Locations
 */
import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import type { ModalOptions } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { ViewBasePage } from '@shared/pages';
import { AddressModalPage } from '@shared/pages';
import type { TLocation, TRoutePoint, TRoutesViewModel, TAddressModalViewModel } from '@shared/models';

@Component({
    selector: 'app-route3Locations',
    templateUrl: './route3Locations.page.html',
    styleUrls: ['./route3Locations.page.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, IonicModule]
})
export class Route3LocationsPage extends ViewBasePage implements OnInit, OnDestroy {
    public vm: TRoutesViewModel;
    public amvm: TAddressModalViewModel;
    private dealershipLocation: TLocation;
    public startingLocation: TLocation;
    public endingLocation: TLocation;

    // ================================================
    // Lifecycle Methods
    // ================================================
    constructor(injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_ROUTE_PAGE3);
    }

    public override ngOnInit(): void {
        super.ngOnInit();

        this.vm = super.getViewModel();
        this.dealershipLocation = this.sessionService.getDealershipLocation();

        // ✅ SIMPLIFIED: Initialize with dealership as default
        this.amvm = {
            title: '',
            optionLabel1: 'Start Route At Dealership',
            optionLabel2: 'Start Route At Custom Location',
            dealershipLocation: this.utilsService.deepClone(this.dealershipLocation),

            // Default both to dealership
            startingLocation: this.utilsService.deepClone(this.dealershipLocation),
            endingLocation: this.utilsService.deepClone(this.dealershipLocation),

            startingSource: 'dealership',
            endingSource: 'dealership',
            position: 'start',
            addressVerified: true
        };

        // Default both to dealership
        this.startingLocation = this.utilsService.deepClone(this.dealershipLocation);
        this.endingLocation = this.utilsService.deepClone(this.dealershipLocation);
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    // ================================================
    // Navigation
    // ================================================

    /**
     * Handle Next button click
     */
    public async onNextClick(): Promise<void> {
        try {
            this.updateVmFromAmvm();
            super.setViewModel(this.vm);
            await this.router.navigateByUrl('/routes/route4Ordering');
        } catch (error) {
            // console.error('Error navigating to route ordering:', error);
            await this.uxHelperService.showAlert('Error', 'Failed to proceed to next step');
        }
    }

    /**
     * Update main view model from address modal view model
     */
    private updateVmFromAmvm(): void {
        this.vm.startingPoint.location = this.amvm.startingLocation;
        this.vm.startingPoint.atDealership = (this.amvm.startingSource === 'dealership');
        this.vm.startingPoint.fromDealership = true;
        this.vm.startingPoint.letter = '?';

        this.vm.endingPoint.location = this.amvm.endingLocation;
        this.vm.endingPoint.atDealership = (this.amvm.endingSource === 'dealership');
        this.vm.endingPoint.fromDealership = false;
        this.vm.endingPoint.letter = '?';
    }

    // ================================================
    // Display Methods
    // ================================================

    public isStartingRouteAtDealership(): string {
        return this.amvm?.startingSource === 'dealership' ? 'Yes' : 'No';
    }

    public isEndingRouteAtDealership(): string {
        return this.amvm?.endingSource === 'dealership' ? 'Yes' : 'No';
    }

    // ================================================
    // Modal Prompts
    // ================================================

    /**
     * Prompt user for starting location
     */
    public async onStartingLocationClick(): Promise<void> {
        const modalOptions: ModalOptions = {
            component: AddressModalPage,
            componentProps: {
                addressModalViewModel: {
                    ...this.amvm,
                    'title': 'Starting Route Location',
                    'optionLabel1': 'Start Route At Dealership',
                    'optionLabel2': 'Start Route At Custom Location',
                    'position': 'start'
                }
            },
            backdropDismiss: false,
            cssClass: 'addressModalSize'
        };

        const modal = await this.modalController.create(modalOptions);
        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data?.okay) {
            // Update amvm with returned data
            this.amvm = data.addressModalViewModel;

            // Update display location
            if (this.amvm.startingSource === 'dealership') {
                this.startingLocation = this.amvm.dealershipLocation;
            } else {
                this.startingLocation = this.amvm.startingLocation;
            }
        }
    }

    /**
     * Prompt user for ending location
     */
    public async onEndingLocationClick(): Promise<void> {
        const modalOptions: ModalOptions = {
            component: AddressModalPage,
            componentProps: {
                addressModalViewModel: {
                    ...this.amvm,
                    'title': 'Ending Route Location',
                    'optionLabel1': 'End Route At Dealership',
                    'optionLabel2': 'End Route At Custom Location',
                    'position': 'end'
                }
            },
            backdropDismiss: false,
            cssClass: 'addressModalSize'
        };

        const modal = await this.modalController.create(modalOptions);
        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data?.okay) {
            // Update amvm with returned data
            this.amvm = data.addressModalViewModel;

            // Update display location
            if (this.amvm.endingSource === 'dealership') {
                this.endingLocation = this.amvm.dealershipLocation;
            } else {
                this.endingLocation = this.amvm.endingLocation;
            }
        }
    }
}
